import { z } from 'zod';
import {
    InputModelWithSource,
    LayoutSettingsBaseModel,
    BasicLayoutSettingsModel,
    LayersLayoutSettingsModel,
    LayersWithSectionsLayoutSettingsModel,
    SectionsLayoutSettingsModel,
    BasicNodeModel,
    LayersNodeModel,
    LayersWithSectionsNodeModel,
    SectionsNodeModel,
    NodeBaseModel,
    ComparisonNodeBaseModel,
    BasicComparisonNodeModel,
    LayersComparisonNodeModel
} from '../models';
import { LayerRange, SectionRange, SectionSyncRuleDto } from '../types';
import { SectionSyncManager } from '.';

export default class InstanceUtils {
    static numberOfLayersSchema = z.object({
        numberOfLayers: z.number()
    });

    static numberOfSectionsSchema = z.object({
        numberOfSections: z.number()
    });

    static layerRangeSchema = z.object({
        minNumberOfLayers: z.number(),
        maxNumberOfLayers: z.number()
    });

    static sectionRangeSchema = z.object({
        minNumberOfSections: z.number(),
        maxNumberOfSections: z.number()
    });

    static hasLayers(value: unknown): value is z.infer<typeof InstanceUtils.numberOfLayersSchema> {
        return InstanceUtils.numberOfLayersSchema.safeParse(value).success;
    }

    static hasSections(value: unknown): value is z.infer<typeof InstanceUtils.numberOfSectionsSchema> {
        return InstanceUtils.numberOfSectionsSchema.safeParse(value).success;
    }

    static hasLayerRange(value: unknown): value is LayerRange {
        return InstanceUtils.layerRangeSchema.safeParse(value).success;
    }

    static hasSectionRange(value: unknown): value is SectionRange {
        return InstanceUtils.sectionRangeSchema.safeParse(value).success;
    }

    static isInputWithSource(value: unknown): value is InputModelWithSource {
        return value instanceof InputModelWithSource;
    }

    static isLayoutSettings(value: unknown): value is LayoutSettingsBaseModel {
        return value instanceof LayoutSettingsBaseModel;
    }

    static isBasicLayoutSettings(value: unknown): value is BasicLayoutSettingsModel {
        return value instanceof BasicLayoutSettingsModel;
    }

    static isLayersLayoutSettings(value: unknown): value is LayersLayoutSettingsModel {
        return value instanceof LayersLayoutSettingsModel;
    }

    static isLayersWithSectionsLayoutSettings(value: unknown): value is LayersWithSectionsLayoutSettingsModel {
        return value instanceof LayersWithSectionsLayoutSettingsModel;
    }

    static isSectionsLayoutSettings(value: unknown): value is SectionsLayoutSettingsModel {
        return value instanceof SectionsLayoutSettingsModel;
    }

    static isBasicNode(value: unknown): value is BasicNodeModel {
        return value instanceof BasicNodeModel;
    }

    static isLayersNode(value: unknown): value is LayersNodeModel {
        return value instanceof LayersNodeModel;
    }

    static isLayersWithSectionsNode(value: unknown): value is LayersWithSectionsNodeModel {
        return value instanceof LayersWithSectionsNodeModel;
    }

    static isSectionsNode(value: unknown): value is SectionsNodeModel {
        return value instanceof SectionsNodeModel;
    }

    static isComparisonNode(value: unknown): value is ComparisonNodeBaseModel {
        return value instanceof ComparisonNodeBaseModel;
    }

    static isBasicComparisonNode(value: unknown): value is BasicComparisonNodeModel {
        return value instanceof BasicComparisonNodeModel;
    }

    static isLayersComparisonNode(value: unknown): value is LayersComparisonNodeModel {
        return value instanceof LayersComparisonNodeModel;
    }

    static hasSectionSyncRules(value: unknown): value is NodeBaseModel & { sectionSyncRules: SectionSyncRuleDto[] } {
        return InstanceUtils.isSectionsNode(value) && !!value.sectionSyncRules && value.sectionSyncRules.length > 0;
    }

    static hasSectionSyncManager(value: unknown): value is NodeBaseModel & { sectionSyncManager: SectionSyncManager } {
        return InstanceUtils.isSectionsNode(value) && !!value.sectionSyncManager;
    }
}
